import { parsePhoneNumber, type Country } from "react-phone-number-input";

/**
 * SNS(카카오/네이버)가 내려준 전화번호 원본을 가입 데이터 형식으로 분해한다.
 *
 * 저장 형식은 IdentityGlobalPhoneForm.buildCtn 과 동일한 규칙을 따른다 —
 * 전 국가 공통으로 libphonenumber 의 national number ("1012345678", "6505551234").
 *
 * 파싱 실패(형식 불명/유효하지 않은 번호/번호 미제공)는 빈 값으로 떨어뜨려
 * prefill 없이 사용자가 직접 입력하는 경로로 흐르게 한다. — 잘못된 값을 채워 넣는 것보다 안전하다.
 */
export interface ParsedSnsPhone {
    /** 저장/발송용 국내 번호 */
    ctn: string;
    /** 국가 전화코드 ("+82") */
    ctnCountryCd: string;
    /** ISO2 국가 코드 ("KR") — Step4 국가 선택 초기값. 국가코드 역산보다 정확하다
     *  (+1 은 US/CA 포함 25개국이 공유하므로 calling code 만으로는 특정 불가). */
    ctnCountry: Country | "";
}

const EMPTY: ParsedSnsPhone = { ctn: "", ctnCountryCd: "", ctnCountry: "" };

/**
 * @param raw E.164 또는 국가코드가 포함된 표기 (카카오 "+82 10-1234-5678", 네이버 mobile_e164 "+821012345678")
 *            국가코드 없는 표기("010-1234-5678")는 국가 판별이 불가능하므로 파싱 실패로 처리된다.
 */
export const parseSnsPhone = (raw?: string | null): ParsedSnsPhone => {
    const trimmed = (raw ?? "").trim();
    if (!trimmed) return EMPTY;

    // parsePhoneNumber 는 파싱 불가 시 throw 대신 undefined 를 반환하지만,
    // 방어적으로 try 로 감싼다 (라이브러리 버전에 따른 동작 차이 대비).
    let parsed;
    try {
        parsed = parsePhoneNumber(trimmed);
    } catch {
        return EMPTY;
    }
    if (!parsed || !parsed.country || !parsed.isValid()) return EMPTY;

    const national = parsed.nationalNumber;
    return {
        ctn: national,
        ctnCountryCd: `+${parsed.countryCallingCode}`,
        ctnCountry: parsed.country,
    };
};
